// src/components/Navbar.jsx

import React, { useState, useEffect, useMemo } from "react";
import { Toolbar, Button, Menu, MenuItem, Box } from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import SocioLoginModal from "./SocioLogin/SocioLoginModal";
import "../styles/navbar.css";
import eventGif from "../assets/img/btnlive.gif";

/* ======================================================
📌 CONFIGURACIÓN CENTRAL DE MENÚ (DRY)
====================================================== */

const DEFAULT_SITE_CONFIG = {
  evento_link: "",
  directorio_visible: false,
  comites_visible: false,
};

const Navbar = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openSocioLogin, setOpenSocioLogin] = useState(false);
  const [siteConfig, setSiteConfig] = useState(DEFAULT_SITE_CONFIG);

  const isActive = (path) => location.pathname === path;
  const eventLink = siteConfig.evento_link;
  const navItems = useMemo(
    () =>
      [
        { label: "Inicio", path: "/" },
        {
          label: "Nosotros",
          desktopExtra: { label: "Misión y visión", path: "/nosotros" },
          children: [
            siteConfig.directorio_visible && {
              label: "Directorio",
              path: "/nosotros/directorio",
            },
            {
              label: "Rep. de divisiones técnicas",
              path: "/nosotros/rep-divisiones-tecnicas",
            },
            // { label: "Socios", path: "/nosotros/guiasocios" },
          ].filter(Boolean),
        },
        { label: "Noticias", path: "/noticias" },
        { label: "Cursos", path: "/cursos" },
        { label: "Eventos", path: "/eventos" },
        siteConfig.comites_visible && {
          label: "Comités Técnicos",
          path: "/comites-tecnicos",
        },
        { label: "Contacto", path: "/contacto" },
      ].filter(Boolean),
    [siteConfig]
  );

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "unset";
    return () => (document.body.style.overflow = "unset");
  }, [mobileOpen]);

  useEffect(() => {
    const fetchSiteConfig = async () => {
      try {
        const { data } = await api.get("/site-config/");
        setSiteConfig({
          evento_link: data?.evento_link || "",
          directorio_visible: Boolean(data?.directorio_visible),
          comites_visible: Boolean(data?.comites_visible),
        });
      } catch (error) {
        setSiteConfig(DEFAULT_SITE_CONFIG);
      }
    };

    fetchSiteConfig();
  }, []);

  if (location.pathname.startsWith("/dashboard")) return null;

  /* ======================================================
  🖥️ MENÚ DESKTOP
  ====================================================== */
  const renderDesktopMenu = () =>
    navItems.map((item) => {
      if (item.children) {
        return (
          <React.Fragment key={item.label}>
            <Button
              color="inherit"
              onClick={handleMenuOpen}
              className={
                location.pathname.includes("/nosotros") ? "active" : ""
              }
            >
              {item.label}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  backgroundColor: "#ffffff",
                  color: "#1a1a2e",
                  borderRadius: "10px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  minWidth: 220,
                  mt: 1,
                },
              }}
            >
              {/* SOLO DESKTOP */}
              {item.desktopExtra && (
                <MenuItem
                  component={Link}
                  to={item.desktopExtra.path}
                  onClick={handleMenuClose}
                  sx={{ fontWeight: 600 }}
                >
                  {item.desktopExtra.label}
                </MenuItem>
              )}

              {item.children.map((child) => (
                <MenuItem
                  key={child.path}
                  component={Link}
                  to={child.path}
                  onClick={handleMenuClose}
                >
                  {child.label}
                </MenuItem>
              ))}
            </Menu>
          </React.Fragment>
        );
      }

      return (
        <Button
          key={item.path}
          component={Link}
          to={item.path}
          className={isActive(item.path) ? "active" : ""}
        >
          {item.label}
        </Button>
      );
    });

  /* ======================================================
  📱 MENÚ MOBILE (DRAWER)
  ====================================================== */
  const renderDrawerMenu = () =>
    navItems.flatMap((item) =>
      item.children
        ? [
            <span key={item.label} className="drawer-section-title">
              {item.label}
            </span>,
            item.desktopExtra && (
              <Link
                key={item.desktopExtra.path}
                to={item.desktopExtra.path}
                className={`drawer-nav-item ${
                  isActive(item.desktopExtra.path) ? "active" : ""
                }`}
              >
                {item.desktopExtra.label}
              </Link>
            ),
            ...item.children.map((child) => (
              <Link
                key={child.path}
                to={child.path}
                className={`drawer-nav-item ${
                  isActive(child.path) ? "active" : ""
                }`}
              >
                {child.label}
              </Link>
            )),
          ].filter(Boolean)
        : [
            <Link
              key={item.path}
              to={item.path}
              className={`drawer-nav-item ${
                isActive(item.path) ? "active" : ""
              }`}
            >
              {item.label}
            </Link>,
          ]
    );

  return (
    <>
      <nav className="navbar">
        <Toolbar className="navbar-toolbar">
          <Box className="navbar-left">
            <button className="navbar-hamburger" onClick={handleDrawerToggle}>
              <MenuIcon />
            </button>

            <Link to="/" className="navbar-logo">
              <img
                src="/media/logo-abis-lp.png"
                alt="Logo ABIS LP"
                className="navbar-logo-img"
              />
            </Link>

            <Box className="navbar-menu">{renderDesktopMenu()}</Box>
          </Box>

          <Box className="navbar-right">
            {eventLink && (
              <a href={eventLink} target="_blank" rel="noopener noreferrer">
                <img src={eventGif} alt="Evento" className="gif-evento" />
              </a>
            )}

            {!isAuthenticated ? (
              <>
                <Button
                  variant="outlined"
                  onClick={() => setOpenSocioLogin(true)}
                >
                  Aprobación de Proyectos
                </Button>
                <Button
                  variant="contained"
                  component={Link}
                  to="/solicitud-registro-socios"
                >
                  Solicitud Registro Socios
                </Button>
              </>
            ) : (
              <Button variant="contained" component={Link} to="/dashboard">
                Ir al Dashboard
              </Button>
            )}
          </Box>
        </Toolbar>
      </nav>

      {/* ===================== DRAWER MOBILE ===================== */}
      <div
        className={`navbar-drawer-overlay ${mobileOpen ? "open" : ""}`}
        onClick={handleDrawerToggle}
      />

      <aside className={`navbar-drawer ${mobileOpen ? "open" : ""}`}>
        <div className="drawer-header">
          <img src="/media/logo-abis-lp.png" alt="Logo ABIS LP" />
          <button className="drawer-close" onClick={handleDrawerToggle}>
            <CloseIcon />
          </button>
        </div>

        {eventLink && (
          <div className="drawer-event-gif">
            <img src={eventGif} alt="Evento" />
          </div>
        )}

        <nav className="drawer-nav">{renderDrawerMenu()}</nav>

        <div className="drawer-divider" />

        <div className="drawer-actions">
          {!isAuthenticated ? (
            <>
              <Button
                variant="outlined"
                className="drawer-button outlined"
                onClick={() => setOpenSocioLogin(true)}
              >
                Aprobación de Proyectos
              </Button>
              <Link
                to="/solicitud-registro-socios"
                className="drawer-button contained"
              >
                Solicitud Registro Socios
              </Link>
            </>
          ) : (
            <Link to="/dashboard" className="drawer-button contained">
              Ir al Dashboard
            </Link>
          )}
        </div>
      </aside>

      <SocioLoginModal
        open={openSocioLogin}
        onClose={() => setOpenSocioLogin(false)}
      />
    </>
  );
};

export default Navbar;
